import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, Maximize2, ChevronDown } from "lucide-react";
import { photos } from "../../data/photos";

export default function Gallery() {
  const [openIdx, setOpenIdx] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const close = useCallback(() => setOpenIdx(null), []);
  const prev = useCallback(
    () =>
      setOpenIdx((i) => (i === null ? null : (i - 1 + photos.length) % photos.length)),
    []
  );
  const next = useCallback(
    () => setOpenIdx((i) => (i === null ? null : (i + 1) % photos.length)),
    []
  );

  // Keyboard navigation when lightbox is open
  useEffect(() => {
    if (openIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [openIdx, close, prev, next]);

  const hasPhotos = photos.length > 0;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative py-24 lg:py-32 px-6 lg:px-10 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header / dropdown toggle */}
        <button
          type="button"
          data-testid="gallery-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between gap-6 group text-left border-b border-white/10 pb-6"
          aria-expanded={expanded}
          aria-controls="gallery-content"
        >
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-10 bg-[#0055FF]" />
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#0055FF]">
                The Gym, In Pictures
              </span>
            </div>
            <h2
              data-testid="gallery-heading"
              className="font-heading font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            >
              Photos
              <span className="text-white/40"> · {photos.length}</span>
            </h2>
          </div>
          <div
            className={`flex-shrink-0 w-12 h-12 lg:w-14 lg:h-14 border border-white/15 flex items-center justify-center transition-all duration-300 group-hover:border-[#0055FF] group-hover:bg-[#0055FF] ${
              expanded ? "rotate-180" : "rotate-0"
            }`}
          >
            <ChevronDown
              size={22}
              className="text-white"
              strokeWidth={2.5}
            />
          </div>
        </button>

        {/* Collapsible content */}
        <div
          id="gallery-content"
          className={`grid transition-[grid-template-rows,opacity] duration-500 ease-out ${
            expanded ? "grid-rows-[1fr] opacity-100 mt-10" : "grid-rows-[0fr] opacity-0 mt-0"
          }`}
        >
          <div className="overflow-hidden">
            {hasPhotos ? (
              <div
                data-testid="gallery-grid"
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
              >
                {photos.map((photo, i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setOpenIdx(i)}
                    className="group relative aspect-square border border-white/10 overflow-hidden bg-[#0F0F11] hover:border-[#0055FF] transition-colors"
                    aria-label={`View photo ${i + 1}: ${photo.caption || ""}`}
                  >
                    <img
                      src={photo.src}
                      alt={photo.caption || `JerseyFit Barbell photo ${i + 1}`}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors" />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-11 h-11 bg-[#0055FF] flex items-center justify-center">
                        <Maximize2 size={18} className="text-white" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-md px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/90">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div
                data-testid="gallery-empty"
                className="border border-white/10 bg-[#0F0F11] p-10 lg:p-14 text-center"
              >
                <p className="text-white/70 text-base font-medium">
                  Photos coming soon. Add your photos in{" "}
                  <code className="text-[#0055FF] font-bold">
                    /app/frontend/src/data/photos.js
                  </code>
                  .
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {openIdx !== null && hasPhotos && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Photo viewer"
          onClick={close}
        >
          {/* Close */}
          <button
            type="button"
            data-testid="gallery-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            className="absolute top-4 right-4 w-12 h-12 border border-white/20 bg-black/60 hover:bg-[#0055FF] hover:border-[#0055FF] flex items-center justify-center transition-colors z-10"
            aria-label="Close"
          >
            <X size={22} className="text-white" />
          </button>

          {/* Prev */}
          <button
            type="button"
            data-testid="gallery-prev-btn"
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-12 h-12 lg:w-14 lg:h-14 border border-white/20 bg-black/60 hover:bg-[#0055FF] hover:border-[#0055FF] flex items-center justify-center transition-colors z-10"
            aria-label="Previous photo"
          >
            <ChevronLeft size={26} className="text-white" />
          </button>

          {/* Image */}
          <div
            className="relative max-w-[92vw] max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={openIdx}
              src={photos[openIdx].src}
              alt={photos[openIdx].caption || `JerseyFit Barbell photo ${openIdx + 1}`}
              className="max-w-full max-h-[78vh] object-contain border border-white/10 animate-[jfb-rise_0.4s_ease-out]"
            />
            <div className="mt-4 flex items-center gap-4 text-white/70">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF]">
                {String(openIdx + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* Next */}
          <button
            type="button"
            data-testid="gallery-next-btn"
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-12 h-12 lg:w-14 lg:h-14 border border-white/20 bg-black/60 hover:bg-[#0055FF] hover:border-[#0055FF] flex items-center justify-center transition-colors z-10"
            aria-label="Next photo"
          >
            <ChevronRight size={26} className="text-white" />
          </button>
        </div>
      )}
    </section>
  );
}
